/**
 * Pre-render scrub for unquoted mermaid edge labels and node text.
 *
 * Mirrors the backend `app/agent/content_scrub.py:fix_mermaid_unquoted_labels`.
 * Backend scrubs the LLM output before persistence so new messages are clean,
 * but old messages already in the DB still have raw `|@future callout|` style
 * labels that break mermaid rendering. This frontend pass catches them on the
 * way to the renderer.
 *
 * The mermaid lexer treats `@`, `(`, `)`, `[`, `]`, `{`, `}`, `#`, `&`, `;`,
 * `,`, `*` as special tokens at the top level. They're only safe inside a
 * `text` lexer state — which mermaid enters when it sees a `"`. Quoting bypasses
 * all the special-char tokens at once.
 */

const SPECIAL_CHARS = /[@()\[\]{}#&;,*<>]/;

function quoteEdgeLabel(_match: string, body: string): string {
  // Already-quoted labels stay as-is
  if (body.startsWith('"')) return _match;
  // Escape any embedded `"` so the wrapper is well-formed
  const escaped = body.replace(/"/g, '\\"');
  return `|"${escaped}"|`;
}

function quoteNodeText(prefix: string, body: string, suffix: string): string {
  if (body.startsWith('"')) return `${prefix}${body}${suffix}`;
  // Only quote if the body contains characters that would confuse the lexer
  if (!SPECIAL_CHARS.test(body)) return `${prefix}${body}${suffix}`;
  const escaped = body.replace(/"/g, '\\"');
  return `${prefix}"${escaped}"${suffix}`;
}

/**
 * Scrub all mermaid code blocks in the input string.
 * Non-mermaid code blocks are unchanged.
 */
export function fixMermaidUnquotedLabels(text: string): string {
  if (!text || !text.includes("```mermaid")) return text;

  return text.replace(/```mermaid\n([\s\S]*?)\n```/g, (_match, body: string) => {
    // xychart-beta uses [...] for numeric data arrays — quoting their contents
    // (because they contain commas) turns numbers into strings and breaks the parser.
    // Skip the node-text quoting pass entirely for xychart diagrams.
    const isXyChart = body.trimStart().startsWith("xychart-beta");

    let scrubbed = body;
    if (!isXyChart) {
      // Edge labels: |...| where the body doesn't already start with "
      scrubbed = scrubbed.replace(/\|([^|\n]+?)\|/g, quoteEdgeLabel);
      // Node text: A[text], A(text), A{text} — only when the body has special chars
      scrubbed = scrubbed.replace(/(\[)([^\]\n]+?)(\])/g, (_m, p, b, s) =>
        quoteNodeText(p, b, s),
      );
      scrubbed = scrubbed.replace(/(\()([^)\n]+?)(\))/g, (_m, p, b, s) =>
        quoteNodeText(p, b, s),
      );
      scrubbed = scrubbed.replace(/(\{)([^}\n]+?)(\})/g, (_m, p, b, s) =>
        quoteNodeText(p, b, s),
      );
    }
    return "```mermaid\n" + scrubbed + "\n```";
  });
}
